import { ActorRef, assign, createMachine, DoneInvokeEvent, send, sendParent, spawn } from 'xstate';
import fixWebmDuration from 'fix-webm-duration';
import { EVENTS as MICROPHONE_EVENTS } from '../../interfaces/microphoneInterface';
import { microphoneMachine } from './microphoneMachine';
import { isFireFox, isMobile } from '../../utils/device.utils';
import { ERROR_ENUM, MEDIA_RECORDER_STATES, recorderErrors } from '../../utils/recorderErrors.utils';
import { EVENTS as WIDGET_EVENTS } from '../../interfaces/widgetInterface';
import {
  ACTIONS,
  EVENTS,
  GUARDS,
  mediaConstrains,
  MIME_TYPES,
  RecorderInterface,
  SERVICES,
  SPEED_TEST_LEVEL,
  STATES,
} from '../../interfaces/recorderInterface';
import { DEFAULT_COUNT_DOWN, DEFAULT_DURATION, RETAKE_SPEED } from '../../utils/constants.utils';
import { SentryTrackingFn } from '../../utils/sentry.utils';
import { registerCountEvent, registerDateEvent } from '../../services/session.service';
import { counterMachine } from './counterMachine';
import { emitTrackEvent, EVENT_TYPES } from '../../services/event.service';

const initialState:RecorderInterface = {
  // mediaStream
  mediaStream: undefined,
  facingMode: { exact: 'user' },
  audioDevices: [],
  videoDevices: [],
  videoRef: { current: null },
  selectedVideoDevice: undefined,
  selectedAudioDevice: undefined,
  constraint: mediaConstrains.hd,
  speedTestLevel: SPEED_TEST_LEVEL.HIGH,
  // actor
  microphoneRef: null,
  countDownRef: null,
  // mediaRecorder
  mediaRecorder: undefined,
  mimeType: '',
  mimeTypes: [MIME_TYPES.WEBM, MIME_TYPES.MP4],
  videoBitsPerSecond: 2500000,
  countdown: DEFAULT_COUNT_DOWN,
  recordingTime: 0,
  durations: DEFAULT_DURATION,
  // mods
  isMobile: false,
  isSafeMode: false,
  isAutoStart: false,
  isMicError: false,
  isActivelyStopped: false,
};

export const recorderMachineV2 = createMachine<RecorderInterface, DoneInvokeEvent<any>>(
  {
    predictableActionArguments: true,
    id: 'recorder',
    initial: STATES.WAITING,
    context: initialState,
    entry: [ACTIONS.SET_MIC_MACHINE, ACTIONS.SET_PERMISSION_LISTENER],
    exit: [ // Cleanup when component will unmount
      ACTIONS.STOP_MEDIA_STREAM,
      ACTIONS.STOP_MICROPHONE_MACHINE,
    ],
    // global events !!  THOSE events triggered from all possible states!!!!
    on: {
      [EVENTS.SEND_RECORDER_TO_WAITING_STATE]: {
        actions: [ACTIONS.STOP_MEDIA_RECORDER,
          ACTIONS.STOP_MICROPHONE_MACHINE,
          ACTIONS.STOP_MEDIA_STREAM],
        target: [STATES.WAITING],
      },
      // when received info from mic, tell parent , that all good
      // its works for IDEL and RECORDING STATES
      [EVENTS.MIC_DATA_RECEIVED]: {
        actions: [ACTIONS.SEND_SUCCESS_TO_PARENT, ACTIONS.RESUME_MEDIA_RECORDER, ACTIONS.REMOVE_MIC_ERROR],
      },
      [EVENTS.RECORDER_DEVICE_ERROR]: [
        // permission denied error, can be triggered when user denied permission
        // at browser level or pc level.
        {
          cond: GUARDS.IS_NOT_ALLOWED_ERROR,
          actions: [
            ACTIONS.SEND_ERROR_TO_PARENT,
            ACTIONS.STOP_MEDIA_STREAM,
            ACTIONS.STOP_MICROPHONE_MACHINE,
          ],
          target: [STATES.ERROR],
        },
        // No sound from mic, it can be muted mic from system ( only Chrome detection )
        // or bad quality of sound
        {
          cond: GUARDS.NO_SOUND_FROM_MIC_MACHINE,
          // fallback for mic error, we have to store an error , since error is happening before mediaRecorder init (SET_MIC_ERROR)
          actions: [ACTIONS.SEND_ERROR_TO_PARENT, ACTIONS.PAUSE_MEDIA_RECORDER, ACTIONS.SET_MIC_ERROR],
        },
        // if no special condition go to error state, and send error to parent
        {
          target: [STATES.ERROR],
          actions: [ACTIONS.SEND_ERROR_TO_PARENT, { type: ACTIONS.SENTRY, data: { level: 'error' } }],
        },
      ],
    },
    states: {
      [STATES.INIT]: {
        invoke: {
          src: SERVICES.GET_MEDIA_STREAM,
          onDone: {
            target: [STATES.IDLE],
            actions: [
              send(
                (_context, _event) => ({
                  type: MICROPHONE_EVENTS.ON_DETECTION_SPEED,
                  data: RETAKE_SPEED.MEDIUM,
                }),
                { to: (context) => context.microphoneRef as ActorRef<DoneInvokeEvent<any>> },
              ),
              ACTIONS.SET_MEDIA_STREAM_CORE,
              ACTIONS.SEND_MEDIA_STREAM_TO_MIC_MACHINE,
              { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_camera_setup', type: 'date' } },
              { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_microphone_setup', type: 'date' } },
            ],
          },
          onError: [
            // if something wrong with params for mediaStream , try to init it with safe mode
            {
              target: [STATES.INIT],
              cond: GUARDS.IS_ERROR_WITH_WRONG_PARAMS,
              actions: [{ type: ACTIONS.SENTRY, data: { level: 'info' } }, ACTIONS.SET_SAFE_MODE,
                { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_camera_error', type: 'date' } },
                { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_microphone_error', type: 'date' } },
              ],
            },
            {
              target: [STATES.ERROR],
              actions: [{ type: ACTIONS.SENTRY, data: { level: 'fatal' } }, ACTIONS.SEND_ERROR_TO_PARENT,
                { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_camera_error', type: 'date' } },
                { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_microphone_error', type: 'date' } },
              ],
            },
          ],
        },
      },
      [STATES.IDLE]: {
        always: [
          {
          // if isAutoStart , we have to start recording without preparation
            cond: GUARDS.IS_AUTO_START,
            target: [STATES.RECORDING],
          },
        ],
        // before we exit idle state , we have to disabled auto_start variable
        exit: [ACTIONS.DISABLED_AUTO_START],
        on: {
          [EVENTS.SET_MIC_SPEED_DETECTION]: {
            actions: [send(
              (_context, _event) => ({
                type: MICROPHONE_EVENTS.ON_DETECTION_SPEED,
                data: RETAKE_SPEED.DISABLED,
              }),
              { to: (context) => context.microphoneRef as ActorRef<DoneInvokeEvent<any>> },
            )],
          },
          // start recording
          [EVENTS.START_RECORDING]: {
            target: [STATES.RECORDING],
          },
          // change video device, mic + mediaStream should be stopped
          [EVENTS.CHANGE_VIDEO_DEVICE]: {
            target: [STATES.INIT],
            actions: [
              ACTIONS.STOP_MEDIA_STREAM,
              ACTIONS.STOP_MICROPHONE_MACHINE,
              ACTIONS.CHANGE_VIDEO_DEVICE,
              { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_change_camera_setup', type: 'date' } },
            ],
          },
          // change audio device, mic + mediaStream should be stopped
          [EVENTS.CHANGE_AUDIO_DEVICE]: {
            target: [STATES.INIT],
            actions: [
              ACTIONS.STOP_MEDIA_STREAM,
              ACTIONS.STOP_MICROPHONE_MACHINE,
              ACTIONS.CHANGE_AUDIO_DEVICE,
              { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_change_mic_setup', type: 'date' } },
            ],
          },
          // parent update question params (require naming change)
          [EVENTS.SET_QUESTION_PARAMS]: {
            actions: [ACTIONS.SET_QUESTION_PARAMS],
          },
          // parent update ,according speed test update quality of the video (require naming change)
          [EVENTS.SPEED_TEST_RESULT_UPDATED]: {
            target: [STATES.INIT],
            cond: GUARDS.IS_QUALITY_CHANGED,
            actions: [
              ACTIONS.STOP_MEDIA_STREAM,
              ACTIONS.STOP_MICROPHONE_MACHINE,
              ACTIONS.UPDATE_CONSTRAINT,
            ],
          },
          // if new devices (connected / disconnected) update list
          [EVENTS.UPDATE_DEVICES_LIST]: {
            actions: [ACTIONS.UPDATE_AUDIO_AND_VIDEO_DEVICES],
          },
          // at this state we have to reset everything except videoRef, microphoneRef, duration, constant
          [EVENTS.UPDATE_CURRENT_DEVICE]: {
            target: [STATES.INIT],
            actions: [
              ACTIONS.STOP_MEDIA_STREAM,
              ACTIONS.STOP_MICROPHONE_MACHINE,
              ACTIONS.RESET_MACHINE,
            ],
          },
        },
      },
      [STATES.RECORDING]: {
        initial: STATES.COUNT_DOWN,
        states: {
          [STATES.COUNT_DOWN]: {
            entry: [send(
              (_context, _event) => ({
                type: MICROPHONE_EVENTS.ON_DETECTION_SPEED,
                data: RETAKE_SPEED.DISABLED,
              }),
              { to: (context) => context.microphoneRef as ActorRef<DoneInvokeEvent<any>> },
            ), { type: ACTIONS.INIT_COUNT_DOWN_ACTOR, data: { callback: EVENTS.UPDATE_TIMER } }],
            // we need to stay with this check , coz if user click for FORCE_START, take(context.countdown) stay static
            always: [
              {
                target: STATES.COLLECTING_BLOBS,
                actions: [ACTIONS.STOP_COUNT_DOWN_ACTOR],
                cond: GUARDS.IS_TIME_TO_START_RECORDING,
              },
            ],
            on: {
              [EVENTS.UPDATE_TIMER]: {
                actions: [ACTIONS.UPDATE_COUNT_DOWN],
              },
              [EVENTS.FORCE_START]: {
                actions: [ACTIONS.SET_FORCE_START],
              },
            },
          },
          [STATES.COLLECTING_BLOBS]: {
            type: 'parallel',
            states: {
              [STATES.RECORDER_TIMER]: {
                entry: [send(
                  (_context, _event) => ({
                    type: MICROPHONE_EVENTS.ON_DETECTION_SPEED,
                    data: RETAKE_SPEED.FAST,
                  }),
                  { to: (context) => context.microphoneRef as ActorRef<DoneInvokeEvent<any>> },
                ), { type: ACTIONS.INIT_COUNT_DOWN_ACTOR, data: { callback: EVENTS.RECORDING_UPDATE_TIMER } }],
                on: {
                  [EVENTS.RECORDING_UPDATE_TIMER]: {
                    cond: GUARDS.IS_RECORDER_ACTIVE,
                    actions: [ACTIONS.UPDATE_RECORDING_TIME],
                  },
                },
              },
              [STATES.RECORDER_SENDING_BLOBS]: {
                invoke: {
                  src: SERVICES.GET_MEDIA_RECORDER,
                },
                // if video duration === recording time, stop recording and stop mediaStream
                // !!!! its really important to understand that  always be triggered 10000x times, since GUARDS.IS_TIME_TO_STOP_RECORDING going to be true
                // so everything what we are going to do here ( not ideal ) set recordingTime to 0, so  to be sure that GUARDS.IS_TIME_TO_STOP_RECORDING no longer true
                always: [
                  {
                    cond: GUARDS.IS_TIME_TO_STOP_RECORDING, // SHOULD BE CALLED ONLY ONES
                    actions: [
                      ACTIONS.STOP_COUNT_DOWN_ACTOR,
                      ACTIONS.STOP_MEDIA_RECORDER,
                      ACTIONS.STOP_MICROPHONE_MACHINE,
                      ACTIONS.STOP_MEDIA_STREAM,
                    ],
                  },
                ],
                on: {
                  [EVENTS.STOP_RECORDING]: {
                    actions: [
                      ACTIONS.SET_ACTIVELY_STOPPED,
                      ACTIONS.STOP_COUNT_DOWN_ACTOR,
                      ACTIONS.STOP_MEDIA_RECORDER,
                      ACTIONS.STOP_MICROPHONE_MACHINE,
                      ACTIONS.STOP_MEDIA_STREAM,
                    ],
                  },
                  // save mediaRecorder into the context
                  [EVENTS.SET_MEDIA_RECORDER]: {
                    actions: [ACTIONS.SET_MEDIA_RECORDER],
                  },
                  // EVENTS.CHANGE_VIDEO_DEVICE && EVENTS.CHANGE_AUDIO_DEVICE
                  // change video device during recording(error happens)
                  // AUTO_START should be triggered to be able to start recording right after device change
                  // First we clean state, after set isAutoStart
                  [EVENTS.CHANGE_VIDEO_DEVICE]: {
                    target: `#recorder.${STATES.INIT}`,
                    actions: [
                      ACTIONS.STOP_MEDIA_STREAM,
                      ACTIONS.STOP_COUNT_DOWN_ACTOR,
                      ACTIONS.STOP_MICROPHONE_MACHINE,
                      ACTIONS.CHANGE_VIDEO_DEVICE,
                      ACTIONS.UPDATE_MACHINE_FOR_NEW_QUESTION,
                      ACTIONS.SET_AUTO_START,
                    ],
                  },
                  [EVENTS.CHANGE_AUDIO_DEVICE]: {
                    target: `#recorder.${STATES.INIT}`,
                    actions: [
                      ACTIONS.STOP_MEDIA_STREAM,
                      ACTIONS.STOP_COUNT_DOWN_ACTOR,
                      ACTIONS.STOP_MICROPHONE_MACHINE,
                      ACTIONS.CHANGE_AUDIO_DEVICE,
                      ACTIONS.UPDATE_MACHINE_FOR_NEW_QUESTION,
                      ACTIONS.SET_AUTO_START,
                    ],
                  },
                  [EVENTS.SEND_CURRENT_TAKE]: [
                    {
                      cond: GUARDS.IS_VIDEO_CORRUPTED,
                      target: `#recorder.${STATES.ERROR}`,
                      actions: [
                        ACTIONS.SEND_CURRENT_TAKE,
                        { type: ACTIONS.SEND_ERROR_TO_PARENT, data: recorderErrors.VideoCorrupted },
                      ],
                    },
                    {
                      target: `#recorder.${STATES.WAITING}`,
                      actions: [ACTIONS.SEND_CURRENT_TAKE],
                    },
                  ],
                  [EVENTS.UPDATE_CURRENT_DEVICE]: {
                    target: `#recorder.${STATES.ERROR}`,
                    actions: [
                      ACTIONS.STOP_MEDIA_STREAM,
                      ACTIONS.STOP_COUNT_DOWN_ACTOR,
                      ACTIONS.STOP_MICROPHONE_MACHINE,
                      ACTIONS.RESET_MACHINE,
                    ],
                  },
                  [EVENTS.PAUSE_MEDIA_RECORDER]: {
                    actions: [ACTIONS.PAUSE_MEDIA_RECORDER],
                  },
                },
              },
            },
          },
        },
      },
      [STATES.WAITING]: {
        id: 'waiting',
        on: {
        // need to be renamed
          [EVENTS.RE_INIT]: {
            target: STATES.INIT,
            actions: [ACTIONS.UPDATE_MACHINE_FOR_NEW_QUESTION],
          },
        },
      },
      [STATES.ERROR]: {
        id: 'errorState',
        entry: [(_, event) => (event.data.name === recorderErrors.NotAllowedError.name) && emitTrackEvent({ eventType: 'permissionsDenied' })],
        on: {
        // need to be renamed
          [EVENTS.RETRY]: {
            target: [STATES.INIT],
            actions: [ACTIONS.STOP_MEDIA_STREAM, ACTIONS.STOP_MICROPHONE_MACHINE, ACTIONS.RESET_MACHINE],
          },
          [EVENTS.RE_INIT]: {
            target: [STATES.INIT],
            actions: [ACTIONS.STOP_MEDIA_STREAM,
              ACTIONS.STOP_MICROPHONE_MACHINE,
              ACTIONS.SEND_SUCCESS_TO_PARENT,
              ACTIONS.RESET_MACHINE],
          },
        },
      },
    },
  },
  {
    services: {
      [SERVICES.GET_MEDIA_STREAM]: (context) => async (callback) => {
        try {
          const audioDeviceId = context.selectedAudioDevice && context.selectedAudioDevice.deviceId;
          const videoDeviceId = context.selectedVideoDevice && context.selectedVideoDevice.deviceId;
          const isVideoOff = () => !!context.recordWithoutVideo;
          const isMobileAspectForced = context.forcedView === 'mobile';
          const isDesktopAspectForced = context.forcedView === 'desktop';

          const defaultConstraints = {
            video: !isVideoOff(),
            audio: true,
          };
          const mobileConstraints = {
            audio: {
              deviceId: audioDeviceId ? { exact: audioDeviceId } : undefined,
            },
            video: isVideoOff() ? false : {
              ...(!isDesktopAspectForced && context.constraint.video),
              facingMode: context.facingMode,
              aspectRatio: isDesktopAspectForced ? 9 / 16 : 4 / 3,
            },
          };
          const desktopConstraints = {
            audio: {
              deviceId: audioDeviceId ? { exact: audioDeviceId } : undefined,
            },
            video: isVideoOff() ? false : {
              ...(!isMobileAspectForced && context.constraint.video),
              deviceId: videoDeviceId ? { exact: videoDeviceId } : undefined,
              aspectRatio: isMobileAspectForced ? 3 / 4 : 16 / 9,
            },
          };
          const constraintCheck = (): MediaStreamConstraints => {
            if (context.isSafeMode) return defaultConstraints;
            if (isMobile) return mobileConstraints;
            return desktopConstraints;
          };

          const mediaStream = await navigator.mediaDevices.getUserMedia(constraintCheck());
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoDevices = devices.filter(({ kind }) => kind === 'videoinput');
          const audioDevices = devices.filter(({ kind }) => kind === 'audioinput');
          let currentVideo: MediaTrackCapabilities;
          let currentAudio: MediaTrackCapabilities;

          if (!isVideoOff() && !isFireFox) currentVideo = mediaStream.getVideoTracks()[0].getCapabilities();
          if (!isFireFox) currentAudio = mediaStream.getAudioTracks()[0].getCapabilities();
          const selectedVideoDevice = (!isFireFox && !isVideoOff()) ? videoDevices
            .filter((device) => device.deviceId === currentVideo.deviceId)[0] : undefined; // TODO: use find instead of filter
          const selectedAudioDevice = !isFireFox ? audioDevices
            .filter((device) => device.deviceId === currentAudio.deviceId)[0] : undefined; // TODO: use find instead of filter

          if (context.videoRef.current) {
            context.videoRef.current.srcObject = mediaStream;
          }

          const [track] = isVideoOff() ? mediaStream.getAudioTracks() : mediaStream.getVideoTracks();
          const permissionName = isVideoOff() ? 'microphone' : 'camera';

          track.onended = (_event) => {
            if ('permissions' in navigator && 'query' in navigator.permissions && !isFireFox) {
              navigator.permissions.query({ name: permissionName as PermissionName })
                .then((permissionStatus) => {
                  if (permissionStatus.state === 'denied') {
                    callback({ type: 'RECORDER_DEVICE_ERROR', data: recorderErrors.NotAllowedError });
                  }
                });
            }
          };

          navigator.mediaDevices.ondevicechange = async () => {
            const newDevices = await navigator.mediaDevices.enumerateDevices();
            const newVideoDevices = newDevices.filter(({ kind }) => kind === 'videoinput');
            const newAudioDevices = newDevices.filter(({ kind }) => kind === 'audioinput');
            if ((mediaStream.getAudioTracks()[0].readyState === 'ended' && context!.audioDevices?.length !== newAudioDevices.length)
                || (!isVideoOff() && (
                  (mediaStream.getVideoTracks()[0].readyState === 'ended' && context!.videoDevices?.length !== newVideoDevices.length)
                    || (mediaStream.getAudioTracks()[0].readyState === 'ended' && context!.audioDevices?.length !== newAudioDevices.length)))) {
              callback({ type: EVENTS.UPDATE_CURRENT_DEVICE, data: recorderErrors.NotAllowedError });
            }
            callback({ type: EVENTS.UPDATE_DEVICES_LIST, data: { newVideoDevices, newAudioDevices } });
          };
          return {
            mediaStream, videoDevices, audioDevices, selectedVideoDevice, selectedAudioDevice,
          };
        } catch (error: any) {
          return Promise.reject(recorderErrors[error.name] || recorderErrors.UndefinedError);
        }
      },
      [SERVICES.GET_MEDIA_RECORDER]: (context) => async (callback) => {
        const { mediaStream, mimeType, videoBitsPerSecond } = context;
        let startTime:number;
        let pausedTime:number;
        let resumeTime:number;
        const chunks:Blob[] = [];
        let webm: File | null;
        let superBuffer: Blob | null;
        const options = { mimeType, videoBitsPerSecond };
        try {
          const mediaRecorder = new MediaRecorder(mediaStream as MediaStream, options);
          mediaRecorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
              chunks.push(event.data);
            } else {
              console.error('0 size', event.data, event.data.size);
            }
          };
          mediaRecorder.onstop = () => {
            const videoLength = Date.now() - (startTime + ((resumeTime - pausedTime) || 0));
            // see https://bugs.chromium.org/p/chromium/issues/detail?id=642012
            const fixVideoMetadata = (rawVideoBlob: Blob) => {
              const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
              if (isSafari) {
                return Promise.resolve(rawVideoBlob);
              }
              return fixWebmDuration(rawVideoBlob, videoLength, { logger: false });
            };
            superBuffer = new Blob(chunks, {
              type: mimeType,
            });
            if (!superBuffer.size) {
              SentryTrackingFn(
                { type: '0 size file', data: { superBuffer } },
                { audioDevice: context.selectedAudioDevice, videoDevice: context.selectedVideoDevice, data: { superBuffer }, context },
                'recorder',
                'fatal',
              );
            }
            fixVideoMetadata(superBuffer)
              .then((blob) => {
                webm = new File([blob], Date.now().toString(), {
                  type: mimeType,
                });
                callback({ type: EVENTS.SEND_CURRENT_TAKE, data: { webm, videoLength } });
              })
              .catch((error) => {
                console.error('fixVideoMetadata', error);
              })
              .finally(() => {
                chunks.length = 0;
                superBuffer = null;
                webm = null;
              });
          };
          mediaRecorder.onstart = () => {
            startTime = Date.now();
          };
          mediaRecorder.onpause = () => {
            pausedTime = Date.now();
          };
          mediaRecorder.onresume = () => {
            resumeTime = Date.now();
          };
          mediaRecorder.start(10000);
          callback({ type: EVENTS.SET_MEDIA_RECORDER, data: mediaRecorder });
          emitTrackEvent({ eventType: 'cameraInitialized', extraData: { label: context.selectedVideoDevice?.label } });
          if (context.isMicError) callback(EVENTS.PAUSE_MEDIA_RECORDER);
          return () => {
            // if (mediaRecorder?.state !== 'inactive') mediaRecorder?.stop();
          };
        } catch (error) {
          return Promise.reject(error);
        }
      },
    },
    actions: {
      [ACTIONS.SET_PERMISSION_LISTENER]: (_) => {
        if ('permissions' in navigator && 'query' in navigator.permissions && !isFireFox) {
          navigator.permissions.query({ name: 'camera' as PermissionName }).then((permissionStatus) => {
            permissionStatus.onchange = () => {
              if (permissionStatus.state === 'granted') {
                emitTrackEvent({ eventType: 'permissionsGranted' });
              }
            };
          });
        }
      },
      [ACTIONS.INIT_COUNT_DOWN_ACTOR]: assign({
        countDownRef: (context, event, meta) => spawn(counterMachine.withContext({
          ...counterMachine.context,
          callback: (meta.action as any).data.callback,
        }), { name: 'countDownRef' }),
      }),
      [ACTIONS.STOP_COUNT_DOWN_ACTOR]: assign((context) => {
        (context.countDownRef as any).stop();
        return {
          countDownRef: null,
        };
      }),
      [ACTIONS.SET_ACTIVELY_STOPPED]: assign((context) => ({ isActivelyStopped: true })),
      [ACTIONS.SESSION_EVENT]: (context, event, meta) => {
        if (meta.action.data.type === 'date') registerDateEvent(meta.action.data.event as any).then(() => {});
        if (meta.action.data.type === 'number') registerCountEvent(meta.action.data.event as any).then(() => {});
      },
      [ACTIONS.SENTRY]: (context, event, meta) => SentryTrackingFn(event, context, event.data.name
          || 'recorderMachine', meta.action.data?.level || 'error'),
      // microphone actions
      [ACTIONS.SET_MIC_MACHINE]: assign({
      // set mic machine , should be triggered only ones, we can send null,
      // coz mediaStream is not ready yet , will update it after with (ACTIONS.SEND_MEDIA_STREAM_TO_MIC_MACHINE)
        microphoneRef: (context) => spawn(microphoneMachine.withContext({
          ...microphoneMachine.context,
          mediaStream: context.mediaStream || null,
        }), { name: 'microphone' }),

      }),
      // set core of recorder to the context, it is trigger after success call to (ACTIONS.GET_MEDIA_STREAM)
      // TODO test isSafeMode.
      // also we have to clean isSafeMode value. !!! require some testing ( if user has issue with constraint per each question , what should we do)
      [ACTIONS.SET_MEDIA_STREAM_CORE]: assign({
        selectedVideoDevice: (_, event) => event.data.selectedVideoDevice,
        selectedAudioDevice: (_, event) => event.data.selectedAudioDevice,
        mediaStream: (_, event) => event.data.mediaStream,
        audioDevices: (_, event) => event.data.audioDevices,
        videoDevices: (_, event) => event.data.videoDevices,
        mimeType: (context) => context.mimeTypes.find(window.MediaRecorder.isTypeSupported)
            || MIME_TYPES.WEBM,
        isSafeMode: (_, _event) => false,
      }),
      // if user has an issue with setting parameters to getMediaStream, we define default constant, check function constraintCheck
      [ACTIONS.SET_SAFE_MODE]: assign({
        isSafeMode: (_, _event) => true,
      }),
      // set param for mic error, to be able to detect it before recording will start
      [ACTIONS.SET_MIC_ERROR]: assign({
        isMicError: (_, _event) => true,
      }),
      // force countdown
      [ACTIONS.SET_FORCE_START]: assign({
        countdown: (_, _event) => DEFAULT_COUNT_DOWN,
      }),
      // update question params from parent
      [ACTIONS.SET_QUESTION_PARAMS]: assign({
        countdown: (_, event) => event.data.countdown,
        durations: (_, event) => event.data.duration,
      }),
      // save mediaRecord into the context, only for control  only
      [ACTIONS.SET_MEDIA_RECORDER]: assign({
        mediaRecorder: (context, event) => event.data,
      }),
      [ACTIONS.SET_AUTO_START]: assign({
        isAutoStart: (_, _event) => true,
      }),
      // update mic machine with mediaStream
      [ACTIONS.SEND_MEDIA_STREAM_TO_MIC_MACHINE]: send(
        (context, _event) => ({
          type: MICROPHONE_EVENTS.ON_SET_MEDIA_STREAM,
          data: context.mediaStream,
        }),
        { to: (context) => context.microphoneRef as ActorRef<DoneInvokeEvent<any>> },
      ),
      // send error object to prent, usually happening with permission dined or mic mute
      [ACTIONS.SEND_ERROR_TO_PARENT]: sendParent((context, event) => ({ ...event, type: WIDGET_EVENTS.RECORDER_ERROR })),
      // send success event to the parent, to be able to proceed to the next step
      [ACTIONS.SEND_SUCCESS_TO_PARENT]: sendParent((context, event) => ({ ...event, type: WIDGET_EVENTS.READY_TO_START_RECORDING })),
      // on stop recording send webm file to storage machine
      [ACTIONS.SEND_CURRENT_TAKE]: sendParent(({ isActivelyStopped }, event) => ({ ...event, type: WIDGET_EVENTS.STOP_RECORDING, data: { ...event.data, isActivelyStopped } })),
      // send null param to mic machine to be able to stop sound detecting
      [ACTIONS.STOP_MICROPHONE_MACHINE]: send(
        (_, _event) => ({ type: MICROPHONE_EVENTS.ON_SET_MEDIA_STREAM, data: null }),
        { to: (context) => context.microphoneRef as ActorRef<DoneInvokeEvent<any>> },
      ),
      // clean mic error, this is really sensitive, !! a lot of user can't start recording coz of it
      [ACTIONS.REMOVE_MIC_ERROR]: assign({
        isMicError: (_, _event) => false,
      }),
      // stop mediaStream, we do not want to have a lot of instance of this API
      [ACTIONS.STOP_MEDIA_STREAM]: assign((context) => {
        context.mediaStream?.getTracks().forEach((track) => {
          track.stop();
        });
        if (context.videoRef.current) {
          context.videoRef.current.srcObject = null;
        }
        return {
          mediaStream: undefined,
        };
      }),
      // if there is a problem with mic , we have to pause recorder
      [ACTIONS.PAUSE_MEDIA_RECORDER]: assign({
        mediaRecorder: ({ mediaRecorder }, _) => {
          if (mediaRecorder && (mediaRecorder.state === MEDIA_RECORDER_STATES.RECORDING)) {
            mediaRecorder.pause();
          }
          return mediaRecorder;
        },
      }),
      // resume recording, once all issue fixed
      [ACTIONS.RESUME_MEDIA_RECORDER]: assign({
        mediaRecorder: ({ mediaRecorder }, _) => {
          if (mediaRecorder && (mediaRecorder.state === MEDIA_RECORDER_STATES.PAUSED)) {
            mediaRecorder.resume();
          }
          return mediaRecorder;
        },
      }),
      [ACTIONS.CHANGE_VIDEO_DEVICE]: assign(({ facingMode, selectedVideoDevice, videoDevices }, event) => {
        const _facingMode = isMobile ? { exact: facingMode.exact === 'user' ? 'environment' : 'user' } : facingMode;
        const _selectedVideoDevice = isMobile ? selectedVideoDevice : videoDevices?.find((device) => device.deviceId === event.data);
        emitTrackEvent({ eventType: EVENT_TYPES.CAMERA_CHANGED, extraData: { ...(isMobile && { facingMode: _facingMode }), deviceName: _selectedVideoDevice?.label } });
        return {
          facingMode: _facingMode,
          selectedVideoDevice: _selectedVideoDevice,
        };
      }),
      [ACTIONS.CHANGE_AUDIO_DEVICE]: assign(({ selectedAudioDevice, audioDevices }, event) => {
        const _selectedAudioDevice = audioDevices?.find((device) => device.deviceId === event.data);
        emitTrackEvent({ eventType: EVENT_TYPES.MICROPHONE_CHANGED, extraData: { deviceName: _selectedAudioDevice?.label } });
        return {
          selectedAudioDevice: _selectedAudioDevice,
        };
      }),
      [ACTIONS.UPDATE_CONSTRAINT]: assign({
        constraint: (context, event) => {
          if (event.data.speedTestLevel === SPEED_TEST_LEVEL.HIGH) return isMobile ? mediaConstrains.mobile : mediaConstrains.hd;
          if (event.data.speedTestLevel === SPEED_TEST_LEVEL.MEDIUM) return isMobile ? mediaConstrains.mobile2 : mediaConstrains.vga;
          return mediaConstrains.qvga;
        },
        speedTestLevel: (_, event) => event.data.speedTestLevel,
      }),
      // if new device is plug / unplug we have to detect it , and update list of devices
      [ACTIONS.UPDATE_AUDIO_AND_VIDEO_DEVICES]: assign({
        audioDevices: (_, event) => event.data.newAudioDevices,
        videoDevices: (_, event) => event.data.newVideoDevices,
      }),
      [ACTIONS.UPDATE_COUNT_DOWN]: assign({
        countdown: (context) => Math.max(context.countdown - 1, 0),
      }),
      [ACTIONS.UPDATE_RECORDING_TIME]: assign({
        recordingTime: (context) => context.recordingTime + 1,
      }),
      [ACTIONS.UPDATE_MACHINE_FOR_NEW_QUESTION]: assign((context, _event) => ({
        ...initialState,
        selectedAudioDevice: context.selectedAudioDevice,
        selectedVideoDevice: context.selectedVideoDevice,
        videoRef: context.videoRef,
        microphoneRef: context.microphoneRef,
        constraint: context.constraint,
        durations: context.durations,
        facingMode: context.facingMode,
        recordWithoutVideo: context.recordWithoutVideo,
      })),
      [ACTIONS.DISABLED_AUTO_START]: assign({
        isAutoStart: (_, _event) => false,
      }),
      [ACTIONS.RESET_MACHINE]: assign((context, event) => ({
        ...initialState,
        videoRef: context.videoRef,
        microphoneRef: context.microphoneRef,
        constraint: context.constraint,
        durations: context.durations,
        isAutoStart: event.data?.isAutoStart || context.isAutoStart,
        recordWithoutVideo: context.recordWithoutVideo,
      })),
      [ACTIONS.STOP_MEDIA_RECORDER]: assign({
        mediaRecorder: (context) => {
          context?.mediaRecorder?.stop();
          context?.mediaRecorder?.stream.getTracks().forEach((track) => {
            track.stop();
          });
          if (context.videoRef.current) {
            context.videoRef.current.srcObject = null;
          }
          return undefined;
        },
        recordingTime: (_, _event) => 0,
      }),
    },
    guards: {
      [GUARDS.IS_TIME_TO_STOP_RECORDING]: (
        { durations, recordingTime },
        _event,
      ) => durations === recordingTime,
      [GUARDS.IS_RECORDER_ACTIVE]: (context, _event) => (context.mediaRecorder ? context.mediaRecorder.state === MEDIA_RECORDER_STATES.RECORDING : false),
      [GUARDS.IS_TIME_TO_START_RECORDING]: (context, _event) => context.countdown === 0,
      [GUARDS.IS_AUTO_START]: (context, _event) => context.isAutoStart,
      [GUARDS.IS_QUALITY_CHANGED]: (context, event) => event.data.speedTestLevel !== context.speedTestLevel,
      // errors
      [GUARDS.NO_SOUND_FROM_MIC_MACHINE]: (context, event) => [ERROR_ENUM.NoSoundError].includes(event.data.name),
      [GUARDS.IS_NOT_ALLOWED_ERROR]: (context, event) => [ERROR_ENUM.NotAllowedError].includes(event.data.name),
      [GUARDS.IS_ERROR_WITH_WRONG_PARAMS]: (context, event) => [ERROR_ENUM.OverconstrainedError].includes(
        event.data.name,
      ),
      [GUARDS.IS_VIDEO_CORRUPTED]: (_, { data }) => !data.webm.size,
    },
  },
);
